import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Sparkles, X } from 'lucide-react';
import { useMetadataWatchStore, useScreenWatch } from '../model/metadataWatchStore';
import { applyMetadataChange } from '../useMetadataWatch';
import { summarizeDiff } from '../lib/bundleDiff';

/**
 * Ekranın üstünde beliren ince şerit.
 *
 * İki hâli var: tazeleme düzenleme yüzünden bekletiliyorsa "uygula" istemi, tazeleme
 * (otomatik ya da elle) yapıldıysa neyin değiştiğinin özeti. Tek bileşende, çünkü ayrı
 * bileşenler olsaydı şerit kaybolup yeniden belirir ve ekran zıplardı.
 */
export function MetadataChangeBar({ screenId }: { screenId: string | null | undefined }) {
  const qc = useQueryClient();
  const watch = useScreenWatch(screenId);
  const dismiss = useMetadataWatchStore((s) => s.dismiss);
  const [applying, setApplying] = useState(false);

  if (!screenId) return null;
  if (!watch.pending && watch.changes.length === 0) return null;

  const apply = async () => {
    setApplying(true);
    try {
      await applyMetadataChange(qc, screenId);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      role="status"
      className="border-brand-500/30 bg-brand-500/10 text-fg flex shrink-0 items-center gap-2 border-b px-3 py-1.5 text-xs"
    >
      {watch.pending ? (
        <>
          <RefreshCw className="text-brand-600 h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            Bu ekranın tasarımı Octo Lab'da değişti. Düzenlemeniz sürdüğü için beklemede.
          </span>
          <button
            type="button"
            onClick={() => void apply()}
            disabled={applying}
            className="bg-brand-600 hover:bg-brand-700 ml-auto shrink-0 rounded px-2 py-0.5 font-medium text-white disabled:opacity-60"
          >
            {applying ? 'Uygulanıyor…' : 'Uygula'}
          </button>
        </>
      ) : (
        <>
          <Sparkles className="text-brand-600 h-3.5 w-3.5 shrink-0" />
          <span className="truncate" title={watch.changes.map((c) => c.label).join('\n')}>
            {summarizeDiff(watch.changes)}
          </span>
        </>
      )}
      <button
        type="button"
        aria-label="Kapat"
        onClick={() => dismiss(screenId)}
        className="hover:bg-bg-hover text-fg-muted shrink-0 rounded p-0.5"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
